import { useState } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Textarea from '#globalComponents/Forms/Textarea';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { useSetCustomCellValue } from '../../models/mutations';

const VALUE_CLASS = 'block truncate max-w-[220px] text-left';

/**
 * One custom-column cell. Formula cells are computed and read-only; ai cells open a small editor,
 * and a hand-edited value survives later fill runs.
 */
const CustomColumnCell = ({ column, cell, recipientRef }) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState('');
	const setValue = useSetCustomCellValue();
	const value = cell?.value;

	if (column.type === 'formula') {
		return value ? (
			<span className={`${VALUE_CLASS} text-text`} title={value}>
				{value}
			</span>
		) : (
			<span className="text-bgBase-800">-</span>
		);
	}

	const save = async () => {
		await setValue.mutateAsync({ id: column.id, ...recipientRef, value: draft });
		setOpen(false);
	};

	const trigger = (
		<button
			type="button"
			onClick={() => {
				setDraft(value ?? '');
				setOpen((prev) => !prev);
			}}
			className={`${VALUE_CLASS} hover:text-primary-600 transition-colors ${value ? 'text-text' : 'text-bgBase-800'}`}
			title={value ?? 'Wert eintragen'}
		>
			{value ?? '-'}
			{cell?.userEdited && <span className="ml-1 text-[10px] text-bgBase-800">(bearbeitet)</span>}
		</button>
	);

	return (
		<Popover open={open} onClose={() => setOpen(false)} trigger={trigger} size="m" align="start">
			<Panel.Body className="space-y-2">
				<p className="text-xs font-semibold text-text">{column.name}</p>
				<Textarea
					rows={3}
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					maxLength={1000}
					autoFocus
				/>
				<p className="text-[11px] text-bgBase-800">
					Von Hand bearbeitete Werte werden beim erneuten Befüllen nicht überschrieben. Leer lassen, um den
					Wert zu entfernen.
				</p>
				<div className="flex justify-end gap-2">
					<Button size="sm" variant="outline" onClick={() => setOpen(false)}>
						Abbrechen
					</Button>
					<Button size="sm" onClick={save} loading={setValue.isPending}>
						Speichern
					</Button>
				</div>
			</Panel.Body>
		</Popover>
	);
};

CustomColumnCell.propTypes = {
	column: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, type: PropTypes.string }).isRequired,
	cell: PropTypes.shape({ value: PropTypes.string, userEdited: PropTypes.bool }),
	// { crmContactId } or { crmCompanyId } of the row.
	recipientRef: PropTypes.object.isRequired,
};

export default CustomColumnCell;
